/**
 * Build widget URLs based on environment detection
 * Provides a centralized way to determine widget URLs for both preview and public use
 */

/**
 * Detects the current environment based on hostname
 * @returns {'development' | 'staging' | 'production'}
 */
export function detectEnvironment() {
  if (process.env.NODE_ENV === 'development') {
    return 'development';
  }

  const hostname = window.location.hostname.toLowerCase();

  if (hostname.includes('staging')) {
    return 'staging';
  }

  return 'production';
}

/**
 * Get the widget base URL for a specific widget
 * @param {string} widgetName - The name of the widget (e.g., 'guest-check-in', 'lightbox'). Corresponds to the HTML filename in S3 as declared in the pom.
 * @param {number} [devPort] - Optional dev server port (defaults to 5175)
 * @returns {string} The base URL for the widget
 */
export function getWidgetBaseUrl(widgetName, devPort = 5175) {
  const environment = detectEnvironment();

  switch (environment) {
    case 'development':
      return `http://localhost:${devPort}/`;

    case 'staging':
      return `https://files.redchirp.com/v1/prod/widget/staging-${widgetName}.html`;

    case 'production':
    default:
      return `https://files.redchirp.com/v1/prod/widget/${widgetName}.html`;
  }
}

/**
 * Build a complete widget URL with query parameters
 * @param {string} widgetName - The name of the widget
 * @param {Object} [params] - Optional query parameters to add
 * @param {number} [devPort] - Optional dev server port
 * @returns {string} The complete widget URL
 */
export function buildWidgetUrl(widgetName, params = {}, devPort = 5175) {
  const baseUrl = getWidgetBaseUrl(widgetName, devPort);

  if (Object.keys(params).length === 0) {
    return baseUrl;
  }

  const queryParams = new URLSearchParams(params);
  return `${baseUrl}?${queryParams.toString()}`;
}
